/**
 * Smart Defaults System for RepConnect
 * Provides intelligent pre-population and suggestions based on user patterns and preferences
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 */

import { PhysicianPreferences } from './physicianPreferences';
import { MeetingStorage } from './meetingStorage';
import { logger } from './logger';
import { UserStorage } from './userStorage';

export class SmartDefaults {
  /**
   * Get available time slots based on physician's historical preferences
   */
  static getSuggestedTimeSlots(physicianId, requestDate) {
    const timePreferences = PhysicianPreferences.analyzeTimePreferences(physicianId);
    const availableSlots = this.generateAvailableSlots();
    
    // Default time slots for new physicians
    const defaultPreferredSlots = ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM'];
    
    if (timePreferences.totalMeetings === 0) {
      return {
        suggestedTimes: defaultPreferredSlots,
        reason: 'Common lunch meeting times',
        confidence: 'medium'
      };
    }
    
    // Sort available slots by physician's historical preference
    const scoredSlots = availableSlots.map(slot => {
      const preference = timePreferences.preferredTimes.find(p => p.time === slot);
      const score = preference ? preference.count : 0;
      return { time: slot, score, historicalCount: score };
    });
    
    const topSlots = scoredSlots
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(slot => slot.time);
    
    return {
      suggestedTimes: topSlots.length > 0 ? topSlots : defaultPreferredSlots,
      reason: timePreferences.mostCommonTime ? 
        `Based on ${timePreferences.totalMeetings} previous meetings` : 
        'Common lunch meeting times',
      confidence: timePreferences.totalMeetings > 3 ? 'high' : 'medium',
      historicalData: {
        totalMeetings: timePreferences.totalMeetings,
        mostCommon: timePreferences.mostCommonTime
      }
    };
  }

  /**
   * Generate available time slots for meetings
   */
  static generateAvailableSlots() {
    const slots = [];
    const startHour = 11; // 11 AM
    const endHour = 14; // 2 PM
    
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 30) {
        const displayHour = hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const minutesStr = minutes === 0 ? '00' : minutes.toString();
        slots.push(`${displayHour}:${minutesStr} ${ampm}`);
      }
    }
    
    return slots;
  }

  /**
   * Get suggested restaurants for a meeting request
   */
  static getSuggestedRestaurants(physicianId, availableRestaurants) {
    const insights = PhysicianPreferences.getBookingInsights(physicianId, availableRestaurants);
    const recommendations = insights.restaurantRecommendations.slice(0, 3);
    
    return {
      topRecommendations: recommendations,
      summary: insights.summary,
      reasoning: recommendations.map(rec => ({
        name: rec.restaurant.name,
        reason: rec.reason,
        insights: rec.insights,
        priority: rec.priority
      }))
    };
  }

  /**
   * Generate intelligent meeting response templates
   */
  static getMeetingResponseTemplates(physicianId, meetingRequest) {
    const user = UserStorage.getUserById(physicianId);
    const timePrefs = PhysicianPreferences.analyzeTimePreferences(physicianId);
    
    const templates = {
      approval: {
        quick: `Thank you for the meeting request. I'm available for the proposed time and look forward to our discussion.`,
        
        personalized: this.generatePersonalizedApproval(user, meetingRequest, timePrefs),
        
        withPreferences: this.generateApprovalWithPreferences(user, meetingRequest, timePrefs)
      },
      
      decline: {
        polite: `Thank you for your meeting request. Unfortunately, I'm not available at the proposed time. Please feel free to suggest alternative dates.`,
        
        withAlternatives: this.generateDeclineWithAlternatives(user, meetingRequest, timePrefs),
        
        busy: `I appreciate your interest in meeting. My schedule is quite full this week, but I'd be happy to consider future availability.`
      },
      
      reschedule: {
        timeConflict: this.generateRescheduleTemplate(user, meetingRequest, timePrefs),
        
        preferencesBased: this.generatePreferenceBasedReschedule(user, meetingRequest, timePrefs)
      }
    };
    
    return templates;
  }

  /**
   * Generate personalized approval message
   */
  static generatePersonalizedApproval(user, meetingRequest, timePrefs) {
    const greeting = this.getGreetingStyle(user);
    const mostCommonTime = timePrefs.mostCommonTime;
    
    let message = `${greeting} ${meetingRequest.salesRepName},\n\n`;
    message += `Thank you for reaching out regarding ${meetingRequest.products?.[0] || 'your products'}. `;
    message += `The proposed meeting time works well for me.\n\n`;
    
    if (mostCommonTime && mostCommonTime !== meetingRequest.time) {
      message += `While ${meetingRequest.time} works, please note that I typically prefer ${mostCommonTime} for lunch meetings if you have flexibility in the future.\n\n`;
    }
    
    message += `I look forward to our discussion.\n\n`;
    message += `Best regards,\n${user?.name || 'Dr. ' + (user?.lastName || 'Smith')}`;
    
    return message;
  }

  /**
   * Generate approval with dining preferences
   */
  static generateApprovalWithPreferences(user, meetingRequest, timePrefs) {
    const insights = PhysicianPreferences.getBookingInsights(meetingRequest.customerId || user?.id, []);
    const greeting = this.getGreetingStyle(user);
    
    let message = `${greeting} ${meetingRequest.salesRepName},\n\n`;
    message += `I'm available for the meeting as proposed. `;
    
    if (insights.summary.topRestaurant) {
      message += `If you're flexible with the restaurant choice, I've had good experiences at ${insights.summary.topRestaurant.restaurant?.name}.`;
    }
    
    message += `\n\nLooking forward to our meeting.\n\n`;
    message += `Best regards,\n${user?.name || 'Dr. ' + (user?.lastName || 'Smith')}`;
    
    return message;
  }

  /**
   * Generate decline with alternative suggestions
   */
  static generateDeclineWithAlternatives(user, meetingRequest, timePrefs) {
    const suggestedTimes = this.getSuggestedTimeSlots(user?.id, meetingRequest.date);
    const greeting = this.getGreetingStyle(user);
    
    let message = `${greeting} ${meetingRequest.salesRepName},\n\n`;
    message += `Thank you for your meeting request. Unfortunately, ${meetingRequest.time} doesn't work for my schedule.\n\n`;
    
    if (suggestedTimes.suggestedTimes.length > 0) {
      message += `I'm typically available for lunch meetings at these times:\n`;
      suggestedTimes.suggestedTimes.slice(0, 3).forEach(time => {
        message += `• ${time}\n`;
      });
      message += `\nWould any of these times work better for you?\n\n`;
    }
    
    message += `Please let me know your availability.\n\n`;
    message += `Best regards,\n${user?.name || 'Dr. ' + (user?.lastName || 'Smith')}`;
    
    return message;
  }

  /**
   * Generate reschedule template
   */
  static generateRescheduleTemplate(user, meetingRequest, timePrefs) {
    const greeting = this.getGreetingStyle(user);
    const preferredTime = timePrefs.mostCommonTime;
    
    let message = `${greeting} ${meetingRequest.salesRepName},\n\n`;
    message += `I'd like to reschedule our meeting from ${meetingRequest.time}. `;
    
    if (preferredTime) {
      message += `Would ${preferredTime} work better? This time typically fits my schedule well.\n\n`;
    } else {
      message += `Could we consider a different time? I'm generally available between 12:00 PM and 1:30 PM for lunch meetings.\n\n`;
    }
    
    message += `Thank you for your understanding.\n\n`;
    message += `Best regards,\n${user?.name || 'Dr. ' + (user?.lastName || 'Smith')}`;
    
    return message;
  }

  /**
   * Generate preference-based reschedule
   */
  static generatePreferenceBasedReschedule(user, meetingRequest, timePrefs) {
    const greeting = this.getGreetingStyle(user);
    
    let message = `${greeting} ${meetingRequest.salesRepName},\n\n`;
    message += `Thank you for the meeting invitation. I'd prefer to adjust the timing slightly. `;
    
    if (timePrefs.totalMeetings > 0 && timePrefs.mostCommonTime) {
      message += `Based on my typical schedule, ${timePrefs.mostCommonTime} tends to work best for me. `;
    }
    
    message += `Would this timing work for you?\n\n`;
    message += `I look forward to our discussion about ${meetingRequest.products?.[0] || 'your products'}.\n\n`;
    message += `Best regards,\n${user?.name || 'Dr. ' + (user?.lastName || 'Smith')}`;
    
    return message;
  }

  /**
   * Get appropriate greeting style based on user preferences
   */
  static getGreetingStyle(user) {
    // Could be enhanced with user preference settings
    const formal = ['Hello', 'Dear', 'Good morning', 'Good afternoon'];
    const casual = ['Hi', 'Hello', 'Hey'];
    
    // Default to formal for healthcare professionals
    return formal[0];
  }

  /**
   * Get auto-suggestions for meeting approval/decline based on context
   */
  static getContextualSuggestions(physicianId, meetingRequest) {
    const currentTime = new Date();
    const requestTime = new Date(meetingRequest.date + ' ' + meetingRequest.time);
    const hoursUntilMeeting = (requestTime - currentTime) / (1000 * 60 * 60);
    
    const suggestions = {
      urgency: hoursUntilMeeting < 24 ? 'high' : hoursUntilMeeting < 72 ? 'medium' : 'low',
      autoApprovalEligible: false,
      recommendedAction: null,
      reasoning: []
    };

    // Check if rep is in approved list
    const approvedReps = this.getApprovedReps(physicianId);
    const isApprovedRep = approvedReps.some(rep => 
      rep.email === meetingRequest.salesRepEmail ||
      rep.name === meetingRequest.salesRepName
    );

    if (isApprovedRep) {
      suggestions.autoApprovalEligible = true;
      suggestions.recommendedAction = 'approve';
      suggestions.reasoning.push('Representative is on your approved list');
    }

    // Check time preference alignment
    const timePrefs = PhysicianPreferences.analyzeTimePreferences(physicianId);
    const isPreferredTime = timePrefs.preferredTimes.some(pref => 
      pref.time === meetingRequest.time && pref.count > 1
    );

    if (isPreferredTime) {
      suggestions.reasoning.push('Meeting time aligns with your preferences');
      if (!suggestions.recommendedAction) {
        suggestions.recommendedAction = 'approve';
      }
    }

    // Check for scheduling conflicts (would need calendar integration)
    const hasConflict = this.checkScheduleConflict(physicianId, meetingRequest);
    if (hasConflict) {
      suggestions.recommendedAction = 'reschedule';
      suggestions.reasoning.push('Potential scheduling conflict detected');
    }

    return suggestions;
  }

  /**
   * Get approved representatives list
   */
  static getApprovedReps(physicianId) {
    try {
      const approvedRepsData = localStorage.getItem(`approved_reps_${physicianId}`);
      return approvedRepsData ? JSON.parse(approvedRepsData) : [];
    } catch (error) {
      logger.error('Error loading approved reps:', error);
      return [];
    }
  }

  /**
   * Check for potential schedule conflicts
   */
  static checkScheduleConflict(physicianId, meetingRequest) {
    // This would integrate with calendar systems in a real implementation
    // For now, simulate based on existing meeting data
    const existingMeetings = MeetingStorage.getMeetingsForUser(physicianId, 'customer');
    const requestDateTime = new Date(meetingRequest.date + ' ' + meetingRequest.time);
    
    return existingMeetings.some(meeting => {
      const meetingDateTime = new Date(meeting.date + ' ' + meeting.time);
      const timeDiff = Math.abs(meetingDateTime - requestDateTime) / (1000 * 60); // minutes
      return timeDiff < 90; // Conflict if within 1.5 hours
    });
  }

  /**
   * Get smart notification settings suggestions
   */
  static getNotificationSuggestions(physicianId) {
    const meetings = PhysicianPreferences.getPhysicianMeetings(physicianId);
    const avgMeetingsPerMonth = meetings.length > 0 ? meetings.length / 12 : 0;
    
    let frequency = 'daily';
    let urgencyThreshold = 48; // hours
    
    if (avgMeetingsPerMonth > 10) {
      frequency = 'immediate';
      urgencyThreshold = 24;
    } else if (avgMeetingsPerMonth < 3) {
      frequency = 'weekly';
      urgencyThreshold = 72;
    }
    
    return {
      recommendedFrequency: frequency,
      urgencyThreshold,
      reasoning: `Based on your ${meetings.length} completed meetings, ${frequency} notifications seem appropriate`
    };
  }
}

export default SmartDefaults;